import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Send, X, Phone, Mail, User, Wallet } from 'lucide-react';
import { useApi } from '../../utils/useApi';
import { WhatsAppBubble, useTemplatePreview } from './WhatsAppPreview';

interface CampaignCost {
  costPerMessage: number;
  estimatedCost: number;
  availableCredits: number;
  sufficient: boolean;
}

interface WhatsappTemplate {
  id: string;
  title: string;
  purpose?: string;
  language: string;
  templateName: string;
  content: string;
  parameterCount: number;
  sampleParams: Record<string, string>;
}

interface User {
  id: string;
  name: string;
  phoneNo: string;
  email?: string;
}

interface PreviewConfirmProps {
  template: WhatsappTemplate | null;
  audience: any;
  tenantId?: string;
  recipientCount: number;
  excludedCount?: number;
  userParams: Record<string, string>;
  onSend: (audienceList: User[], scheduledAt?: Date | null, audienceModified?: boolean) => void;
  onBack: () => void;
}

export default function PreviewConfirm({
  template,
  audience,
  tenantId,
  userParams,
  onSend,
  onBack
}: PreviewConfirmProps) {
  const [audienceList, setAudienceList] = useState<User[]>([]);
  const [resolvedCount, setResolvedCount] = useState<number>(0);
  const [cost, setCost] = useState<CampaignCost | null>(null);
  const [isCheckingCredits, setIsCheckingCredits] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [scheduleType, setScheduleType] = useState<'immediate' | 'scheduled'>('immediate');
  const [scheduledDateTime, setScheduledDateTime] = useState<string>('');

  const { fetchWithAuth } = useApi();

  // Server-rendered preview: real gym name for gym_data params, the admin's
  // typed message for user_provided params, with WhatsApp markdown (*bold* etc.)
  // rendered by WhatsAppBubble.
  const { previewContent, isLoading: isPreviewLoading } = useTemplatePreview(
    template?.id,
    userParams,
    template?.content
  );

  // Resolve the actual recipient list from the backend — the same resolver the
  // send path uses, so what's shown here is exactly who will receive the message.
  useEffect(() => {
    const fetchAudienceList = async () => {
      if (!audience || !template || !tenantId) return;

      setIsLoading(true);
      setLoadError(false);
      try {
        const response = await fetchWithAuth('/api/notifications/audience-preview', {
          method: 'POST',
          body: JSON.stringify({
            tenant_id: tenantId,
            audience,
            template_id: template.id
          })
        });
        const recipients = (response.recipients || []).map((r: any) => ({
          id: r.id,
          name: r.name,
          phoneNo: r.phone,
          email: r.email || undefined
        }));
        setAudienceList(recipients);
        setResolvedCount(recipients.length);
        setCost(response.cost || null);
      } catch (error) {
        console.error('Error fetching audience list:', error);
        setLoadError(true);
        setAudienceList([]);
        setResolvedCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAudienceList();
  }, [audience, template, tenantId, fetchWithAuth]);

  const handleRemoveUser = (userId: string) => {
    setAudienceList(prev => prev.filter(user => user.id !== userId));
  };

  // Recomputed against the current list so removing recipients lowers the
  // estimate (and can bring an unaffordable campaign back within budget).
  const estimatedCost = cost ? audienceList.length * cost.costPerMessage : null;
  const insufficientCredits = cost != null && estimatedCost != null && cost.availableCredits < estimatedCost;

  // Always ask the backend for the current balance — never decide on a value
  // fetched earlier in the session. Updates the cost card with what it learns.
  const refreshCreditCheck = async (messageCount: number): Promise<boolean> => {
    setIsCheckingCredits(true);
    try {
      const response = await fetchWithAuth('/api/notifications/credit-check', {
        method: 'POST',
        body: JSON.stringify({
          tenant_id: tenantId,
          template_id: template?.id,
          message_count: messageCount
        })
      });
      setCost({
        costPerMessage: response.costPerMessage,
        estimatedCost: response.estimatedCost,
        availableCredits: response.availableCredits,
        sufficient: response.sufficient
      });
      return !!response.sufficient;
    } finally {
      setIsCheckingCredits(false);
    }
  };

  const handleSend = async () => {
    if (audienceList.length === 0) {
      toast.error('No recipients selected. Please add at least one recipient.');
      return;
    }

    let scheduledAt: Date | null = null;
    if (scheduleType === 'scheduled') {
      if (!scheduledDateTime) {
        toast.error('Please pick a date and time for the scheduled send.');
        return;
      }
      const scheduledDate = new Date(scheduledDateTime);
      if (scheduledDate < new Date()) {
        toast.error('Scheduled time must be in the future.');
        return;
      }
      scheduledAt = scheduledDate;
    }

    // Fresh backend check at the moment of sending (the backend enforces this
    // again when the campaign is created and dispatched).
    try {
      const sufficient = await refreshCreditCheck(audienceList.length);
      if (!sufficient) {
        toast.error('Not enough message credits to send this campaign. Please top up or reduce the audience.');
        return;
      }
    } catch (error) {
      toast.error('Could not verify message credits. Please try again.');
      return;
    }

    // If recipients were removed here, the send must target the trimmed list
    // rather than re-resolving the original filter.
    const audienceModified = audienceList.length !== resolvedCount;
    onSend(audienceList, scheduledAt, audienceModified);
  };

  if (!template) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 text-lg mb-4">⚠️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Template Selected</h3>
        <p className="text-gray-600">Please select a template to continue.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Message preview and recipients side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* WhatsApp Preview */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Message</h3>
          <WhatsAppBubble
            content={previewContent}
            isLoading={isPreviewLoading}
            className="max-w-sm"
          />
        </div>

        {/* Recipients */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Recipients</h3>
            <div className="text-sm text-gray-600">
              {audienceList.length} recipient{audienceList.length !== 1 ? 's' : ''}
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading recipients...</p>
            </div>
          ) : audienceList.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {loadError ? 'Could not load recipients. Go back and reselect the audience.' : 'No recipients found'}
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {audienceList.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-white" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">{user.name}</div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {user.phoneNo}
                        </div>
                        {user.email && (
                          <div className="hidden sm:flex items-center gap-1 truncate">
                            <Mail className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{user.email}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveUser(user.id)}
                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                    title="Remove from this campaign"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {audienceList.length > 0 && audienceList.length !== resolvedCount && (
            <p className="text-xs text-amber-600 mt-2">
              {resolvedCount - audienceList.length} recipient{resolvedCount - audienceList.length !== 1 ? 's' : ''} removed — the campaign will only be sent to the list above.
            </p>
          )}
        </div>
      </div>

      {/* Cost Summary */}
      {cost && estimatedCost != null && (
        <div className={`border rounded-lg p-4 ${insufficientCredits ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Wallet className={`h-5 w-5 ${insufficientCredits ? 'text-red-600' : 'text-gray-600'}`} />
              <span className={`font-medium ${insufficientCredits ? 'text-red-900' : 'text-gray-900'}`}>Campaign Cost</span>
            </div>
            <button
              onClick={() => refreshCreditCheck(audienceList.length).catch(() => toast.error('Could not refresh credit balance.'))}
              disabled={isCheckingCredits}
              className={`text-xs font-medium underline-offset-2 hover:underline disabled:opacity-50 ${insufficientCredits ? 'text-red-700' : 'text-blue-600'}`}
            >
              {isCheckingCredits ? 'Refreshing…' : 'Refresh balance'}
            </button>
          </div>
          <div className={`space-y-1 text-sm ${insufficientCredits ? 'text-red-800' : 'text-gray-700'}`}>
            <div>
              Estimated cost: <span className="font-semibold">₹{estimatedCost.toFixed(2)}</span>
              <span className="text-xs ml-1">({audienceList.length} × ₹{cost.costPerMessage.toFixed(2)} per message)</span>
            </div>
            <div>Available credits: <span className="font-semibold">₹{cost.availableCredits.toFixed(2)}</span></div>
            {insufficientCredits && (
              <div className="font-medium mt-2">
                Not enough credits — you need ₹{(estimatedCost - cost.availableCredits).toFixed(2)} more. Top up credits or remove recipients to continue.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Schedule Options */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
        <h4 className="font-medium text-blue-900 mb-3">When to send</h4>

        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="scheduleType"
              value="immediate"
              checked={scheduleType === 'immediate'}
              onChange={(e) => setScheduleType(e.target.value as 'immediate' | 'scheduled')}
              className="w-4 h-4 text-blue-600"
            />
            <span className="text-gray-700">Send immediately</span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="scheduleType"
              value="scheduled"
              checked={scheduleType === 'scheduled'}
              onChange={(e) => setScheduleType(e.target.value as 'immediate' | 'scheduled')}
              className="w-4 h-4 text-blue-600"
            />
            <span className="text-gray-700">Schedule for later</span>
          </label>
        </div>

        {scheduleType === 'scheduled' && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Schedule Date & Time
            </label>
            <input
              type="datetime-local"
              value={scheduledDateTime}
              onChange={(e) => setScheduledDateTime(e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required={scheduleType === 'scheduled'}
            />
            {scheduledDateTime && (
              <p className="mt-1 text-sm text-gray-600">
                Scheduled for: {new Date(scheduledDateTime).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleSend}
          disabled={audienceList.length === 0 || isLoading || isCheckingCredits}
          className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className="h-4 w-4" />
          {isCheckingCredits
            ? 'Checking credits…'
            : scheduleType === 'scheduled'
              ? 'Schedule Campaign'
              : `Send to ${audienceList.length} member${audienceList.length !== 1 ? 's' : ''}`}
        </button>
      </div>

      {/* Validation Messages */}
      {audienceList.length === 0 && !isLoading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="text-red-600">⚠️</div>
            <span className="text-red-800">
              Cannot send campaign: No recipients selected. Please add at least one recipient.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
